/**
 * ImageCapture Component
 * 
 * Provides camera capture (mobile) and gallery/file selection for product images.
 * Supports multiple image selection, preview, accept/retake, and removal.
 * Implements client-side compression for images above threshold.
 * Handles upload to WordPress media library with progress tracking.
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6
 */

import { useState, useRef, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import type { ImageData } from '../types/product';
import type { UploadResult } from '../api/media';
import { uploadImage, deleteImage as deleteMediaImage } from '../api/media';

/** Compression threshold in bytes (500KB) */
export const COMPRESSION_THRESHOLD = 500 * 1024;

/** Compression options for browser-image-compression */
export const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};

export interface ImageCaptureProps {
  images: ImageData[];
  onChange: (images: ImageData[]) => void;
  maxImages?: number;
}

interface CompressionProgress {
  [key: string]: number;
}

interface UploadProgress {
  [key: number]: number;
}

interface UploadError {
  index: number;
  message: string;
}

/**
 * Compresses an image if it exceeds the threshold
 * Returns the compressed file and whether compression occurred
 */
export async function compressImageIfNeeded(
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ file: File; wasCompressed: boolean; originalSize: number; compressedSize: number }> {
  const originalSize = file.size;
  
  if (originalSize <= COMPRESSION_THRESHOLD) {
    return { file, wasCompressed: false, originalSize, compressedSize: originalSize };
  }

  const compressedFile = await imageCompression(file, {
    ...COMPRESSION_OPTIONS,
    onProgress,
  });

  return {
    file: compressedFile,
    wasCompressed: true,
    originalSize,
    compressedSize: compressedFile.size,
  };
}

/**
 * Creates a preview URL for an image file
 */
export function createImagePreview(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Revokes a preview URL to free memory
 */
export function revokeImagePreview(preview: string): void {
  URL.revokeObjectURL(preview);
}

export function ImageCapture({ images, onChange, maxImages = 10 }: ImageCaptureProps) {
  const [compressionProgress, setCompressionProgress] = useState<CompressionProgress>({});
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [uploadErrors, setUploadErrors] = useState<UploadError[]>([]);
  const [capturedImage, setCapturedImage] = useState<ImageData | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleCompressionProgress = useCallback((fileId: string, progress: number) => {
    setCompressionProgress(prev => ({
      ...prev,
      [fileId]: progress,
    }));
  }, []);

  const processAndAddImages = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const remainingSlots = maxImages - images.length;
    const filesToProcess = fileArray.slice(0, remainingSlots);

    const newImages: ImageData[] = [];

    for (const file of filesToProcess) {
      const fileId = `${file.name}-${Date.now()}`;
      
      try {
        handleCompressionProgress(fileId, 0);
        
        const { file: processedFile } = await compressImageIfNeeded(
          file,
          (progress) => handleCompressionProgress(fileId, progress)
        );

        const preview = createImagePreview(processedFile);
        newImages.push({
          file: processedFile,
          preview,
          uploaded: false,
        });
      } finally {
        setCompressionProgress(prev => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
      }
    }

    onChange([...images, ...newImages]);
  }, [images, maxImages, onChange, handleCompressionProgress]);

  /**
   * Upload a single image to WordPress media library
   */
  const uploadSingleImage = useCallback(async (index: number): Promise<boolean> => {
    const image = images[index];
    if (!image || !image.file || image.uploaded) {
      return true;
    }

    // Clear any previous error for this image
    setUploadErrors(prev => prev.filter(e => e.index !== index));
    setUploadProgress(prev => ({ ...prev, [index]: 0 }));

    try {
      const result: UploadResult = await uploadImage(image.file, (progress) => {
        setUploadProgress(prev => ({ ...prev, [index]: progress }));
      });

      // Update image with upload result
      const updatedImages = [...images];
      updatedImages[index] = {
        ...image,
        uploaded: true,
        mediaId: result.id,
      };
      onChange(updatedImages);

      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      setUploadErrors(prev => [...prev.filter(e => e.index !== index), { index, message }]);
      return false;
    } finally {
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  }, [images, onChange]);

  /**
   * Upload all pending images
   */
  const uploadAllImages = useCallback(async (): Promise<{ success: number; failed: number }> => {
    let success = 0;
    let failed = 0;

    for (let i = 0; i < images.length; i++) {
      if (!images[i].uploaded && images[i].file) {
        const result = await uploadSingleImage(i);
        if (result) {
          success++;
        } else {
          failed++;
        }
      }
    }

    return { success, failed };
  }, [images, uploadSingleImage]);

  /**
   * Retry a failed upload
   */
  const retryUpload = useCallback(async (index: number) => {
    await uploadSingleImage(index);
  }, [uploadSingleImage]);



  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      processAndAddImages(files);
    }
    // Reset input to allow selecting the same file again
    event.target.value = '';
  }, [processAndAddImages]);

  const handleCameraCapture = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const preview = createImagePreview(file);
      setCapturedImage({ file, preview, uploaded: false });
      setIsCapturing(true);
    }
    event.target.value = '';
  }, []);

  const handleAcceptCapture = useCallback(async () => {
    if (capturedImage && capturedImage.file) {
      await processAndAddImages([capturedImage.file]);
      revokeImagePreview(capturedImage.preview);
      setCapturedImage(null);
      setIsCapturing(false);
    }
  }, [capturedImage, processAndAddImages]);

  const handleRetakeCapture = useCallback(() => {
    if (capturedImage) {
      revokeImagePreview(capturedImage.preview);
      setCapturedImage(null);
      setIsCapturing(false);
    }
    // Trigger camera again
    cameraInputRef.current?.click();
  }, [capturedImage]);

  const handleCancelCapture = useCallback(() => {
    if (capturedImage) {
      revokeImagePreview(capturedImage.preview);
      setCapturedImage(null);
    }
    setIsCapturing(false);
  }, [capturedImage]);

  const handleRemoveImage = useCallback(async (index: number) => {
    const imageToRemove = images[index];
    if (imageToRemove) {
      revokeImagePreview(imageToRemove.preview);
      
      // If image was uploaded, delete from WordPress media library
      if (imageToRemove.uploaded && imageToRemove.mediaId) {
        try {
          await deleteMediaImage(imageToRemove.mediaId);
        } catch (error) {
          // Log error but still remove from local state
          console.error('Failed to delete image from server:', error);
        }
      }
    }
    
    // Clear any upload error for this image
    setUploadErrors(prev => prev.filter(e => e.index !== index));
    
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  }, [images, onChange]);

  const openCamera = useCallback(() => {
    cameraInputRef.current?.click();
  }, []);

  const openGallery = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const isProcessing = Object.keys(compressionProgress).length > 0;
  const isUploading = Object.keys(uploadProgress).length > 0;
  const canAddMore = images.length < maxImages;
  const pendingUploads = images.filter(img => !img.uploaded && img.file).length;

  // Get upload error for a specific image
  const getUploadError = (index: number): string | undefined => {
    return uploadErrors.find(e => e.index === index)?.message;
  };

  return (
    <div className="image-capture">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-label="Select images from gallery"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        style={{ display: 'none' }}
        aria-label="Capture image with camera"
      />

      {/* Camera capture preview modal */}
      {isCapturing && capturedImage && (
        <div className="capture-preview-modal" role="dialog" aria-label="Review captured image">
          <div className="capture-preview-content">
            <img
              src={capturedImage.preview}
              alt="Captured preview"
              className="capture-preview-image"
            />
            <div className="capture-preview-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAcceptCapture}
              >
                Accept
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleRetakeCapture}
              >
                Retake
              </button>
              <button
                type="button"
                className="btn btn-tertiary"
                onClick={handleCancelCapture}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="image-capture-actions">
        <button
          type="button"
          className="btn btn-camera"
          onClick={openCamera}
          disabled={!canAddMore || isProcessing || isUploading}
          aria-label="Take photo with camera"
        >
          📷 Camera
        </button>
        <button
          type="button"
          className="btn btn-gallery"
          onClick={openGallery}
          disabled={!canAddMore || isProcessing || isUploading}
          aria-label="Select from gallery"
        >
          🖼️ Gallery
        </button>
        {pendingUploads > 0 && (
          <button
            type="button"
            className="btn btn-upload"
            onClick={uploadAllImages}
            disabled={isProcessing || isUploading}
            aria-label={`Upload ${pendingUploads} pending images`}
          >
            ⬆️ Upload ({pendingUploads})
          </button>
        )}
      </div>

      {/* Compression progress indicators */}
      {isProcessing && (
        <div className="compression-progress" role="status" aria-live="polite">
          {Object.entries(compressionProgress).map(([fileId, progress]) => (
            <div key={fileId} className="compression-item">
              <span>Compressing...</span>
              <progress value={progress} max={100} aria-label={`Compression progress: ${progress}%`}>
                {progress}%
              </progress>
            </div>
          ))}
        </div>
      )}

      {/* Image thumbnails */}
      {images.length > 0 && (
        <div className="image-thumbnails" role="list" aria-label="Selected images">
          {images.map((image, index) => {
            const uploadingProgress = uploadProgress[index];
            const error = getUploadError(index);
            const isImageUploading = uploadingProgress !== undefined;

            return (
              <div 
                key={`${image.preview}-${index}`} 
                className={`image-thumbnail ${error ? 'has-error' : ''}`} 
                role="listitem"
              >
                <img
                  src={image.preview}
                  alt={`Product image ${index + 1}`}
                  className="thumbnail-image"
                />
                
                {/* Upload progress overlay */}
                {isImageUploading && (
                  <div className="thumbnail-upload-progress" role="status">
                    <progress 
                      value={uploadingProgress} 
                      max={100} 
                      aria-label={`Upload progress: ${uploadingProgress}%`}
                    >
                      {uploadingProgress}%
                    </progress>
                    <span className="upload-percent">{uploadingProgress}%</span>
                  </div>
                )}

                {/* Error display with retry */}
                {error && !isImageUploading && (
                  <div className="thumbnail-error" role="alert">
                    <span className="error-message" title={error}>⚠️</span>
                    <button
                      type="button"
                      className="btn-retry"
                      onClick={() => retryUpload(index)}
                      aria-label={`Retry upload: ${error}`}
                      title={`Retry: ${error}`}
                    >
                      🔄
                    </button>
                  </div>
                )}

                {/* Remove button */}
                <button
                  type="button"
                  className="thumbnail-remove"
                  onClick={() => handleRemoveImage(index)}
                  disabled={isImageUploading}
                  aria-label={`Remove image ${index + 1}`}
                >
                  ✕
                </button>

                {/* Uploaded indicator */}
                {image.uploaded && !error && (
                  <span className="thumbnail-uploaded" aria-label="Uploaded">✓</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload errors summary */}
      {uploadErrors.length > 0 && (
        <div className="upload-errors" role="alert" aria-live="polite">
          <p className="error-summary">
            {uploadErrors.length} image{uploadErrors.length > 1 ? 's' : ''} failed to upload. 
            Click 🔄 to retry.
          </p>
        </div>
      )}

      {/* Image count indicator */}
      <div className="image-count" aria-live="polite">
        {images.length} / {maxImages} images
        {images.filter(img => img.uploaded).length > 0 && (
          <span className="uploaded-count">
            {' '}({images.filter(img => img.uploaded).length} uploaded)
          </span>
        )}
      </div>
    </div>
  );
}

// Export upload functions for external use (e.g., form submission)
export { uploadImage, uploadImages } from '../api/media';
export type { UploadResult, UploadProgressCallback } from '../api/media';

export default ImageCapture;
